import { useCallback } from "react";
import {
  Question,
  AnswersMap,
  SimpleVisibilityRule,
  VisibilityRuleItem,
  isNestedRule,
  LeadData,
} from "../types/questionTree.types";

/**
 * Hook para evaluar las reglas de visibilidad de las preguntas
 * Determina qué preguntas deben mostrarse basándose en las respuestas actuales y datos del lead
 */
export const useVisibilityEngine = (leadData?: LeadData) => {
  /**
   * Evalúa una regla simple de visibilidad
   */
  const evaluateSimpleRule = useCallback(
    (rule: SimpleVisibilityRule, answers: AnswersMap): boolean => {
      let answerValue: string | number | boolean | undefined;
      const fieldName = rule.field.split("::").pop() || rule.field;

      // Si la regla tiene source "lead", buscar primero en leadData, luego en answers
      if (rule.source === "lead") {
        // Primero buscar en leadData del servidor
        if (leadData && leadData[fieldName] !== undefined) {
          answerValue = leadData[fieldName];
        } else {
          // Si no está en leadData, buscar en answers (respuestas recién dadas)
          answerValue = answers[fieldName] ?? answers[rule.field];
        }
      } else {
        // Buscar la respuesta por el nombre del campo en la regla
        // El campo puede venir como "prp" o como "Lead::prp", así que buscamos ambos
        answerValue = answers[rule.field];

        // Si no se encuentra, intentar buscar por el nombre corto (después de ::)
        if (answerValue === undefined || answerValue === null) {
          answerValue = answers[fieldName];
        }
      }

      // Si aún no hay valor para este campo, la regla no se cumple
      if (answerValue === undefined || answerValue === null) {
        return false;
      }

      // Convertir a string en minúsculas para comparaciones case-insensitive
      const stringValue = String(answerValue).toLowerCase();
      const ruleValue = rule.value.toLowerCase();

      switch (rule.operator) {
        case "=":
          // Soportar respuestas múltiples separadas por coma (select_multiple/checkbox)
          // Si la respuesta es "val1,val2,val3", verificar si rule.value está incluido
          if (stringValue.includes(",")) {
            const values = stringValue.split(",").map((v) => v.trim());
            return values.includes(ruleValue);
          }
          return stringValue === ruleValue;
        case "!=":
          // Para != con múltiples valores, el valor NO debe estar en la lista
          if (stringValue.includes(",")) {
            const values = stringValue.split(",").map((v) => v.trim());
            return !values.includes(ruleValue);
          }
          return stringValue !== ruleValue;
        case ">":
          return Number(answerValue) > Number(rule.value);
        case "<":
          return Number(answerValue) < Number(rule.value);
        case ">=":
          return Number(answerValue) >= Number(rule.value);
        case "<=":
          return Number(answerValue) <= Number(rule.value);
        case "contains":
          // Para checkbox/select_multiple, la respuesta puede ser "val1,val2,val3"
          // o simplemente "val1" si es una sola selección
          const containsValues = stringValue.split(",").map((v) => v.trim());
          return containsValues.includes(ruleValue);
        case "not_contains":
          // Inverso de contains: la respuesta NO debe contener el valor
          const notContainsValues = stringValue.split(",").map((v) => v.trim());
          return !notContainsValues.includes(ruleValue);
        default:
          return false;
      }
    },
    [leadData]
  );

  /**
   * Evalúa una regla (simple o anidada) de forma recursiva
   */
  const evaluateRule = useCallback(
    (rule: VisibilityRuleItem, answers: AnswersMap): boolean => {
      // Si es una regla anidada, evaluar recursivamente
      if (isNestedRule(rule)) {
        const { logic, rules } = rule;
        if (logic === "AND") {
          return rules.every((r) => evaluateRule(r, answers));
        } else {
          return rules.some((r) => evaluateRule(r, answers));
        }
      }

      // Es una regla simple
      return evaluateSimpleRule(rule, answers);
    },
    [evaluateSimpleRule]
  );

  /**
   * Evalúa si una pregunta debe ser visible basándose en sus reglas de visibilidad
   * Prioridad: 1) field/operator/value directo, 2) array de rules
   */
  const isQuestionVisible = useCallback(
    (question: Question, answers: AnswersMap): boolean => {
      // Si no tiene reglas de visibilidad, siempre es visible
      if (!question.visibility) {
        return true;
      }

      const { logic, rules, field, operator, value, source } = question.visibility;

      // Primero: evaluar formato con field/operator/value directamente
      if (field && operator && value !== undefined) {
        const legacyRule: SimpleVisibilityRule = { field, operator, value, source };
        return evaluateSimpleRule(legacyRule, answers);
      }

      // Segundo: evaluar array de rules
      if (rules && rules.length) {
        // Verificar si todas las reglas son simples (sin logic anidado)
        const allSimpleRules = rules.every((rule) => !isNestedRule(rule));

        // Verificar si alguna regla tiene source "lead"
        const hasLeadSourceRules = rules.some(
          (rule) => !isNestedRule(rule) && (rule as SimpleVisibilityRule).source === "lead"
        );

        // Determinar la lógica efectiva:
        // - Si tiene source "lead" o reglas anidadas: respetar el logic original
        // - Si todas las reglas son simples sin source "lead":
        //   - Si usan "contains" o "not_contains" con el mismo value: usar OR (el valor puede estar en cualquier campo)
        //   - Si usan "=" con diferentes values: usar AND (todas las condiciones deben cumplirse)
        let effectiveLogic = logic;

        if (logic === "AND" && allSimpleRules && !hasLeadSourceRules) {
          const simpleRules = rules as SimpleVisibilityRule[];

          // Verificar si todas las reglas buscan el mismo valor con contains/not_contains
          // En ese caso, es OR porque el valor puede estar en cualquier campo
          const allSameValueContains = simpleRules.every(
            (r) => r.operator === "contains" || r.operator === "not_contains"
          ) && new Set(simpleRules.map((r) => r.value)).size === 1;

          if (allSameValueContains) {
            effectiveLogic = "OR";
          }
          // Si no, mantener AND (ej: reglas con "=" buscando diferentes valores)
        }

        if (effectiveLogic === "AND") {
          return rules.every((rule) => evaluateRule(rule, answers));
        } else {
          return rules.some((rule) => evaluateRule(rule, answers));
        }
      }

      // Si no hay ni field ni rules válidas, la pregunta es visible
      return true;
    },
    [evaluateRule, evaluateSimpleRule]
  );

  /**
   * Filtra las preguntas visibles basándose en las respuestas actuales
   */
  const getVisibleQuestions = useCallback(
    (questions: Question[], answers: AnswersMap): Question[] => {
      return questions.filter((question) => isQuestionVisible(question, answers));
    },
    [isQuestionVisible]
  );

  /**
   * Obtiene el índice de la siguiente pregunta visible después del índice actual
   */
  const getNextVisibleQuestionIndex = useCallback(
    (
      questions: Question[],
      answers: AnswersMap,
      currentIndex: number
    ): number | null => {
      for (let i = currentIndex + 1; i < questions.length; i++) {
        if (isQuestionVisible(questions[i], answers)) {
          return i;
        }
      }
      return null; // No hay más preguntas visibles
    },
    [isQuestionVisible]
  );

  /**
   * Obtiene el índice de la pregunta visible anterior al índice actual
   */
  const getPreviousVisibleQuestionIndex = useCallback(
    (
      questions: Question[],
      answers: AnswersMap,
      currentIndex: number
    ): number | null => {
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (isQuestionVisible(questions[i], answers)) {
          return i;
        }
      }
      return null; // No hay preguntas anteriores visibles
    },
    [isQuestionVisible]
  );

  /**
   * Cuenta el total de preguntas visibles
   */
  const countVisibleQuestions = useCallback(
    (questions: Question[], answers: AnswersMap): number => {
      return questions.filter((q) => isQuestionVisible(q, answers)).length;
    },
    [isQuestionVisible]
  );

  /**
   * Obtiene el índice de la pregunta actual dentro de las preguntas visibles
   * Útil para mostrar progreso (ej: "Pregunta 3 de 10")
   */
  const getCurrentVisibleIndex = useCallback(
    (
      questions: Question[],
      answers: AnswersMap,
      currentQuestionId: number
    ): number => {
      const visibleQuestions = getVisibleQuestions(questions, answers);
      return visibleQuestions.findIndex((q) => q.id === currentQuestionId);
    },
    [getVisibleQuestions]
  );

  return {
    isQuestionVisible,
    getVisibleQuestions,
    getNextVisibleQuestionIndex,
    getPreviousVisibleQuestionIndex,
    countVisibleQuestions,
    getCurrentVisibleIndex,
    evaluateRule,
  };
};
